import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { downloadInvoicePdf } from "@/lib/pdf";
import AccountStatement from "@/components/AccountStatement";
import EmailPreviewModal from "@/components/EmailPreviewModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, Trash2, Mail, Phone, MapPin, Search, FileText,
  Download, Pencil, Link2, CheckCircle, RotateCcw,
  ClipboardCopy, ExternalLink, Building2, AlertCircle,
  PoundSterling, X,
} from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

const emptyForm = {
  name: "", tag: "", address: "", email: "", phone: "",
  accounts_contact_name: "", accounts_contact_email: "", accounts_contact_phone: "",
  buyer_name: "", buyer_email: "", buyer_phone: "",
};
const today = () => new Date().toISOString().slice(0, 10);
const fmt = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Live credit bar ───────────────────────────────────────────────────────────
function CreditBar({ limit, outstanding, available, "data-testid": dtid }) {
  const pct = Math.min(100, (outstanding / limit) * 100);
  const barColor = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="mt-1.5 max-w-[220px]" data-testid={dtid}>
      <div className="flex justify-between text-[10px] text-[#5B5C5F] mb-0.5">
        <span>{fmt(outstanding)} used</span>
        <span className={available <= 0 ? "text-red-500 font-semibold" : "text-emerald-600 font-medium"}>
          {fmt(available)} free
        </span>
      </div>
      <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[10px] text-[#5B5C5F] mt-0.5">Limit: {fmt(limit)}</p>
    </div>
  );
}

export default function Accounts() {
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [query, setQuery] = useState("");

  const [statement, setStatement] = useState(null);
  const [stmtOpen, setStmtOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [asOf, setAsOf] = useState(today());
  const [stmtDownloading, setStmtDownloading] = useState(false);
  const stmtRef = useRef(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── Credit usage (live) ───────────────────────────────────────────────────
  const [creditUsage, setCreditUsage] = useState({});

  // ── Pending applications ──────────────────────────────────────────────────
  const [applications, setApplications] = useState([]);
  const [showApplications, setShowApplications] = useState(false);
  const [appViewOpen, setAppViewOpen] = useState(false);
  const [viewedApp, setViewedApp] = useState(null);

  // ── Send form link ────────────────────────────────────────────────────────
  const [formLinkOpen, setFormLinkOpen] = useState(false);
  const [formLinkCustomer, setFormLinkCustomer] = useState(null);
  const [formTokenData, setFormTokenData] = useState(null);
  const [formLinkEmailOpen, setFormLinkEmailOpen] = useState(false);
  const [formLinkEmailSending, setFormLinkEmailSending] = useState(false);

  // ── Accept: step 1 = credit limit dialog, step 2 = email modal ───────────
  const [creditLimitDialogOpen, setCreditLimitDialogOpen] = useState(false);
  const [creditLimitValue, setCreditLimitValue] = useState("");
  const [pendingAcceptApp, setPendingAcceptApp] = useState(null);
  const [acceptEmailOpen, setAcceptEmailOpen] = useState(false);
  const [acceptEmailSending, setAcceptEmailSending] = useState(false);
  const [acceptTarget, setAcceptTarget] = useState(null);

  // ── Revert application ────────────────────────────────────────────────────
  const [revertEmailOpen, setRevertEmailOpen] = useState(false);
  const [revertEmailSending, setRevertEmailSending] = useState(false);
  const [revertTarget, setRevertTarget] = useState(null);

  // ── Remove application ────────────────────────────────────────────────────
  const [removeAppTarget, setRemoveAppTarget] = useState(null); // {customer_id, name}
  const [removingApp, setRemovingApp] = useState(false);

  // ── Edit credit limit ─────────────────────────────────────────────────────
  const [editLimitOpen, setEditLimitOpen] = useState(false);
  const [editLimitCustomer, setEditLimitCustomer] = useState(null);
  const [editLimitValue, setEditLimitValue] = useState("");

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    const { data } = await api.get("/customers");
    setCustomers(data);
  }, []);

  const loadApplications = useCallback(async () => {
    try {
      const { data } = await api.get("/accounts/applications");
      setApplications(data);
    } catch { setApplications([]); }
  }, []);

  const loadCreditUsage = useCallback(async () => {
    try {
      const { data } = await api.get("/accounts/credit-usage");
      const map = {};
      data.forEach((item) => { map[item.customer_id] = item; });
      setCreditUsage(map);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    loadApplications();
    loadCreditUsage();
    const interval = setInterval(loadCreditUsage, 30000);
    return () => clearInterval(interval);
  }, [load, loadApplications, loadCreditUsage]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.phone || "").toLowerCase().includes(q)
    );
  }, [customers, query]);

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/customers/${editingId}`, form);
        toast.success("Account updated");
      } else {
        await api.post("/customers", form);
        toast.success("Account created");
      }
      setForm(emptyForm);
      setEditingId(null);
      setOpen(false);
      load();
    } catch { toast.error("Failed to save account"); }
    finally { setSaving(false); }
  };

  const openNew = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name, tag: c.tag || "", address: c.address || "",
      email: c.email || "", phone: c.phone || "",
      accounts_contact_name: c.accounts_contact_name || "",
      accounts_contact_email: c.accounts_contact_email || "",
      accounts_contact_phone: c.accounts_contact_phone || "",
      buyer_name: c.buyer_name || "",
      buyer_email: c.buyer_email || "",
      buyer_phone: c.buyer_phone || "",
    });
    setOpen(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/customers/${deleteTarget.id}`);
      toast.success("Account deleted");
      setDeleteTarget(null);
      load();
    } catch { toast.error("Failed to delete account"); }
    finally { setDeleting(false); }
  };

  // ── Statement ─────────────────────────────────────────────────────────────
  const fetchStatement = async (id, date) => {
    const { data } = await api.get(`/reports/statement/${id}`, { params: { as_of: date } });
    setStatement(data);
  };

  const openStatement = async (id) => {
    setActiveId(id);
    const d = today();
    setAsOf(d);
    await fetchStatement(id, d);
    setStmtOpen(true);
  };

  useEffect(() => {
    if (stmtOpen && activeId) fetchStatement(activeId, asOf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asOf]);

  const downloadStatement = async () => {
    setStmtDownloading(true);
    try {
      await downloadInvoicePdf(stmtRef.current, `Statement-${statement?.customer?.name || "account"}.pdf`);
      toast.success("Statement downloaded");
    } catch { toast.error("Could not generate statement"); }
    finally { setStmtDownloading(false); }
  };

  // ── Credit Application Flow ───────────────────────────────────────────────
  const openSendFormLink = async (customer) => {
    setFormLinkCustomer(customer);
    setFormTokenData(null);
    setFormLinkOpen(true);
    try {
      const { data } = await api.post(`/accounts/${customer.id}/create-form-link`);
      setFormTokenData(data);
    } catch { toast.error("Failed to generate form link"); setFormLinkOpen(false); }
  };

  const copyFormLink = () => {
    if (formTokenData?.form_url) {
      navigator.clipboard.writeText(formTokenData.form_url);
      toast.success("Link copied to clipboard");
    }
  };

  const handleSendFormLink = async ({ toEmail, subject, customBodyHtml }) => {
    if (!formLinkCustomer) return;
    setFormLinkEmailSending(true);
    try {
      await api.post(`/accounts/${formLinkCustomer.id}/send-form-link`, {
        to_email: toEmail, subject, custom_body_html: customBodyHtml,
      });
      toast.success("Credit application link sent");
      setFormLinkEmailOpen(false);
      setFormLinkCustomer(null);
    } catch { toast.error("Failed to send email"); }
    finally { setFormLinkEmailSending(false); }
  };

  // ── Accept — step 1: credit limit dialog ─────────────────────────────────
  const openAcceptModal = (app) => {
    setPendingAcceptApp(app);
    setCreditLimitValue("");
    setCreditLimitDialogOpen(true);
  };

  const confirmCreditLimit = () => {
    setCreditLimitDialogOpen(false);
    setAcceptTarget({ ...pendingAcceptApp, creditLimit: parseFloat(creditLimitValue) || 0 });
    setAcceptEmailOpen(true);
  };

  // ── Accept — step 2: send email ───────────────────────────────────────────
  const handleAccept = async ({ toEmail, subject, customBodyHtml }) => {
    if (!acceptTarget) return;
    setAcceptEmailSending(true);
    try {
      await api.post(`/accounts/form/${acceptTarget.token}/accept`, {
        to_email: toEmail,
        to_email_2: acceptTarget.buyer_contact_email || "",
        subject,
        custom_body_html: customBodyHtml,
        credit_limit: acceptTarget.creditLimit || 0,
      });
      toast.success("Application accepted and email sent");
      setAcceptEmailOpen(false);
      setAcceptTarget(null);
      setAppViewOpen(false);
      loadApplications();
      load();
      loadCreditUsage();
    } catch { toast.error("Failed to accept application"); }
    finally { setAcceptEmailSending(false); }
  };

  // ── Revert ────────────────────────────────────────────────────────────────
  const openRevertModal = async (app) => {
    try {
      const { data } = await api.post(`/accounts/${app.customer_id}/create-form-link`);
      setRevertTarget({ app, formUrl: data.form_url });
      setRevertEmailOpen(true);
    } catch { toast.error("Could not prepare revert email"); }
  };

  const handleRevert = async ({ toEmail, subject, customBodyHtml }) => {
    if (!revertTarget) return;
    setRevertEmailSending(true);
    try {
      await api.post(`/accounts/form/${revertTarget.app.token}/revert`, {
        to_email: toEmail, subject, custom_body_html: customBodyHtml,
      });
      toast.success("Application reverted and message sent to customer");
      setRevertEmailOpen(false);
      setRevertTarget(null);
      setAppViewOpen(false);
      loadApplications();
    } catch { toast.error("Failed to revert application"); }
    finally { setRevertEmailSending(false); }
  };

  const confirmRemoveApp = async () => {
    if (!removeAppTarget) return;
    setRemovingApp(true);
    try {
      await api.delete(`/accounts/form/${removeAppTarget.customer_id}/remove-application`);
      toast.success("Credit application removed");
      setRemoveAppTarget(null);
      loadApplications();
      load();
      loadCreditUsage();
    } catch { toast.error("Failed to remove application"); }
    finally { setRemovingApp(false); }
  };

  const openEditLimit = (customer) => {
    setEditLimitCustomer(customer);
    setEditLimitValue(customer.credit_limit != null ? String(customer.credit_limit) : "");
    setEditLimitOpen(true);
  };

  const saveEditLimit = async () => {
    if (!editLimitCustomer) return;
    try {
      const limit = editLimitValue.trim() === "" ? null : parseFloat(editLimitValue);
      await api.put(`/accounts/${editLimitCustomer.id}/credit-limit`, { credit_limit: limit });
      toast.success("Credit limit updated");
      setEditLimitOpen(false);
      setEditLimitCustomer(null);
      load();
      loadCreditUsage();
    } catch { toast.error("Failed to update credit limit"); }
  };

  const fmtDt = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="accounts-page">

      {/* ── Pending Applications Banner ─────────────────────────────────── */}
      {applications.length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between gap-4 cursor-pointer"
          onClick={() => setShowApplications((v) => !v)} data-testid="pending-applications-banner">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-[#032352] text-sm">
                {applications.length} Credit Application{applications.length !== 1 ? "s" : ""} Pending Review
              </p>
              <p className="text-xs text-[#5B5C5F]">Click to {showApplications ? "hide" : "review"}</p>
            </div>
          </div>
          <Button size="sm" className="bg-[#032352] hover:bg-[#04347A] text-xs"
            onClick={(e) => { e.stopPropagation(); setShowApplications((v) => !v); }}
            data-testid="review-applications-btn">
            {showApplications ? "Hide" : "Review"}
          </Button>
        </div>
      )}

      {/* ── Applications Review Panel ───────────────────────────────────── */}
      {showApplications && applications.length > 0 && (
        <div className="mb-8 space-y-3" data-testid="applications-review-panel">
          <h2 className="font-heading font-bold text-[#032352] text-lg">Pending Credit Applications</h2>
          {applications.map((app) => (
            <div key={app.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`application-card-${app.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-[#032352] shrink-0" />
                    <span className="font-bold text-[#032352] truncate">{app.trading_name || app.customer_name}</span>
                    {app.trading_style && (
                      <span className="text-xs bg-slate-100 text-[#5B5C5F] px-2 py-0.5 rounded shrink-0">{app.trading_style}</span>
                    )}
                  </div>
                  <p className="text-xs text-[#5B5C5F] mb-1">Account: <span className="font-medium text-[#032352]">{app.customer_name}</span>{app.registration_number && <> &bull; Reg: {app.registration_number}</>}</p>
                  <p className="text-xs text-[#5B5C5F] mb-1">Accounts: <span className="text-[#032352]">{app.accounts_contact_name || "—"}</span>{app.accounts_contact_email && <> ({app.accounts_contact_email})</>}</p>
                  {app.buyer_contact_name && <p className="text-xs text-[#5B5C5F]">Buyer: <span className="text-[#032352]">{app.buyer_contact_name}</span>{app.buyer_contact_email && <> ({app.buyer_contact_email})</>}</p>}
                  <p className="text-xs text-[#5B5C5F] mt-2">Submitted: {fmtDt(app.submitted_at)}</p>
                </div>
                <div className="flex flex-col gap-2 sm:items-end shrink-0">
                  <Button size="sm" variant="outline" className="text-xs border-[#032352] text-[#032352] h-8"
                    onClick={() => { setViewedApp(app); setAppViewOpen(true); }} data-testid={`view-application-${app.id}`}>
                    View Full Application
                  </Button>
                  <Button size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700 h-8"
                    onClick={() => openAcceptModal(app)} data-testid={`accept-application-${app.id}`}>
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Accept
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs border-red-300 text-red-600 hover:bg-red-50 h-8"
                    onClick={() => openRevertModal(app)} data-testid={`revert-application-${app.id}`}>
                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Revert
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs border-slate-300 text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-300 h-8"
                    onClick={() => setRemoveAppTarget({ customer_id: app.customer_id, name: app.trading_name || app.customer_name })}
                    data-testid={`remove-application-${app.id}`}>
                    <X className="w-3.5 h-3.5 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#032352]">Accounts</h1>
          <p className="text-[#5B5C5F] mt-1">Manage your customers and businesses.</p>
        </div>
        <Button data-testid="add-account-button" onClick={openNew} className="bg-[#032352] hover:bg-[#04347A]">
          <Plus className="w-4 h-4 mr-2" /> New Account
        </Button>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent data-testid="account-dialog">
            <DialogHeader><DialogTitle className="font-heading text-[#032352]">{editingId ? "Edit Account" : "New Account"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Company Name</Label>
                  <Input data-testid="account-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Acme Engineering Ltd" />
                </div>
                <div>
                  <Label>Tag <span className="text-xs text-[#5B5C5F] font-normal">(short code)</span></Label>
                  <Input data-testid="account-tag-input" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} placeholder="e.g. TETM" maxLength={12} />
                </div>
              </div>
              <div>
                <Label>Company Address</Label>
                <Textarea data-testid="account-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Unit 4, Industrial Estate..." rows={2} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>General Email</Label>
                  <Input data-testid="account-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="info@acme.co.uk" />
                </div>
                <div>
                  <Label>General Phone</Label>
                  <Input data-testid="account-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="01234 567890" />
                </div>
              </div>

              <div className="border-t border-slate-200 pt-3">
                <p className="text-xs font-semibold text-[#5B5C5F] uppercase tracking-wider mb-3">Accounts Contact</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input data-testid="account-accounts-name-input" value={form.accounts_contact_name} onChange={(e) => setForm({ ...form, accounts_contact_name: e.target.value })} placeholder="Jane Smith" />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input data-testid="account-accounts-email-input" value={form.accounts_contact_email} onChange={(e) => setForm({ ...form, accounts_contact_email: e.target.value })} placeholder="accounts@acme.co.uk" />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input data-testid="account-accounts-phone-input" value={form.accounts_contact_phone} onChange={(e) => setForm({ ...form, accounts_contact_phone: e.target.value })} placeholder="07700 000000" />
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-3">
                <p className="text-xs font-semibold text-[#5B5C5F] uppercase tracking-wider mb-3">Buyer Contact</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input data-testid="account-buyer-name-input" value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} placeholder="John Buyer" />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input data-testid="account-buyer-email-input" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} placeholder="orders@acme.co.uk" />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input data-testid="account-buyer-phone-input" value={form.buyer_phone} onChange={(e) => setForm({ ...form, buyer_phone: e.target.value })} placeholder="07700 111111" />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-account-button" onClick={save} disabled={saving} className="bg-[#032352] hover:bg-[#04347A]">Save Account</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* ── Search ─────────────────────────────────────────────────────────── */}
      <div className="relative w-full max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5B5C5F]" />
        <Input data-testid="accounts-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search accounts..." className="pl-9" />
      </div>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-md border border-slate-200 overflow-x-auto">
        <Table className="min-w-[820px]">
          <TableHeader>
            <TableRow className="bg-[#F8FAFC]">
              <TableHead>Name / Credit</TableHead>
              <TableHead>Tag</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Address</TableHead>
              <TableHead className="w-48 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-[#5B5C5F] py-10">No accounts found.</TableCell></TableRow>
            )}
            {filtered.map((c) => {
              const usage = creditUsage[c.id];
              return (
                <TableRow key={c.id} data-testid={`account-row-${c.id}`}>
                  <TableCell className="font-medium text-[#032352]">
                    <div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {c.name}
                        {c.is_registered_account && (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium leading-none">
                            Registered
                          </span>
                        )}
                      {c.credit_limit > 0 && (
                          <button
                            onClick={() => openEditLimit(c)}
                            data-testid={`credit-limit-badge-${c.id}`}
                            title="Edit credit limit"
                            className="text-xs bg-[#032352]/10 text-[#032352] px-1.5 py-0.5 rounded-full font-medium leading-none flex items-center gap-0.5 hover:bg-[#032352]/20 transition-colors">
                            <PoundSterling className="w-2.5 h-2.5" />{c.credit_limit?.toLocaleString("en-GB")}
                          </button>
                        )}
                        {c.is_registered_account && !c.credit_limit && (
                          <button
                            onClick={() => openEditLimit(c)}
                            data-testid={`set-limit-${c.id}`}
                            title="Set credit limit"
                            className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full font-medium leading-none flex items-center gap-0.5 hover:bg-amber-100 transition-colors">
                            <PoundSterling className="w-2.5 h-2.5" /> Set Limit
                          </button>
                        )}
                      </div>
                      {usage && c.credit_limit > 0 && (
                        <CreditBar
                          limit={usage.credit_limit}
                          outstanding={usage.outstanding}
                          available={usage.available}
                          data-testid={`credit-bar-${c.id}`}
                        />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {c.tag
                      ? <span data-testid={`account-tag-${c.id}`} className="font-mono text-xs bg-[#032352]/10 text-[#032352] px-2 py-0.5 rounded">{c.tag}</span>
                      : <span data-testid={`account-tag-missing-${c.id}`} className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">No tag</span>
                    }
                  </TableCell>
                  <TableCell className="text-[#5B5C5F] text-sm">
                    {c.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3 shrink-0" /> {c.email}</div>}
                    {c.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3 shrink-0" /> {c.phone}</div>}
                    {c.accounts_contact_name && (
                      <div className="mt-1 pt-1 border-t border-slate-100">
                        <p className="text-[10px] text-[#5B5C5F] uppercase tracking-wide">Accounts</p>
                        <p className="text-xs text-[#032352]">{c.accounts_contact_name}</p>
                        {c.accounts_contact_email && <p className="text-xs">{c.accounts_contact_email}</p>}
                        {c.accounts_contact_phone && <p className="text-xs">{c.accounts_contact_phone}</p>}
                      </div>
                    )}
                    {c.buyer_name && (
                      <div className="mt-1 pt-1 border-t border-slate-100">
                        <p className="text-[10px] text-[#5B5C5F] uppercase tracking-wide">Buyer</p>
                        <p className="text-xs text-[#032352]">{c.buyer_name}</p>
                        {c.buyer_email && <p className="text-xs">{c.buyer_email}</p>}
                        {c.buyer_phone && <p className="text-xs">{c.buyer_phone}</p>}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-[#5B5C5F] text-sm max-w-xs">
                    {c.address && <div className="flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5 shrink-0" /> {c.address}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 justify-end">
                      <Button variant="outline" size="sm" data-testid={`statement-account-${c.id}`}
                        onClick={() => openStatement(c.id)} className="border-[#032352] text-[#032352] h-8">
                        <FileText className="w-3.5 h-3.5 mr-1" /> Statement
                      </Button>
                      <button data-testid={`send-form-link-${c.id}`} onClick={() => openSendFormLink(c)}
                        title="Send credit application" className="text-slate-400 hover:text-[#032352] transition-colors">
                        <Link2 className="w-4 h-4" />
                      </button>
                      {c.is_registered_account && (
                        <button
                          data-testid={`remove-registration-${c.id}`}
                          title="Remove registration"
                          onClick={() => setRemoveAppTarget({ customer_id: c.id, name: c.name })}
                          className="text-slate-400 hover:text-red-500 transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                      <button data-testid={`edit-account-${c.id}`} onClick={() => openEdit(c)}
                        className="text-slate-400 hover:text-[#032352] transition-colors">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button data-testid={`delete-account-${c.id}`} onClick={() => setDeleteTarget({ id: c.id, name: c.name })}
                        className="text-slate-400 hover:text-red-500 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* ── Statement Dialog ────────────────────────────────────────────────── */}
      <Dialog open={stmtOpen} onOpenChange={setStmtOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="statement-dialog">
          <DialogTitle className="sr-only">Statement of Account</DialogTitle>
          <DialogDescription className="sr-only">Customer account statement</DialogDescription>
          <div className="flex flex-wrap items-end justify-between gap-3 p-4 pb-0">
            <div>
              <Label className="text-xs">Statement as of</Label>
              <Input type="date" data-testid="statement-asof-input" value={asOf}
                onChange={(e) => setAsOf(e.target.value)} className="w-44 h-9" />
            </div>
            <Button variant="outline" size="sm" onClick={downloadStatement} disabled={stmtDownloading}
              data-testid="download-statement-button" className="border-[#032352] text-[#032352]">
              <Download className="w-4 h-4 mr-2" /> {stmtDownloading ? "Generating..." : "Download PDF"}
            </Button>
          </div>
          <div ref={stmtRef}><AccountStatement statement={statement} /></div>
        </DialogContent>
      </Dialog>

      {/* ── Send Form Link Dialog ─────────────────────────────────────────── */}
      <Dialog open={formLinkOpen} onOpenChange={(o) => { if (!o) setFormLinkOpen(false); }}>
        <DialogContent className="max-w-lg" data-testid="send-form-link-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#032352] font-heading flex items-center gap-2">
              <Link2 className="w-5 h-5" /> Send Credit Application
            </DialogTitle>
            <DialogDescription>Send a unique application link to <strong>{formLinkCustomer?.name}</strong></DialogDescription>
          </DialogHeader>
          {!formTokenData
            ? <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-[#032352] border-t-transparent rounded-full animate-spin" />
                <span className="ml-3 text-[#5B5C5F] text-sm">Generating link...</span>
              </div>
            : <div className="space-y-4 py-2">
                {!formTokenData.app_url_configured && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                    App URL not configured in Settings — the link below may not be fully qualified.
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5B5C5F]">Application Link</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input readOnly value={formTokenData.form_url} className="font-mono text-xs" data-testid="form-link-url" />
                    <button onClick={copyFormLink} data-testid="copy-form-link-btn" title="Copy link"
                      className="text-slate-400 hover:text-[#032352] transition-colors shrink-0">
                      <ClipboardCopy className="w-5 h-5" />
                    </button>
                    <a href={formTokenData.form_url} target="_blank" rel="noopener noreferrer"
                      data-testid="open-form-link-btn" title="Open in new tab"
                      className="text-slate-400 hover:text-[#032352] transition-colors shrink-0">
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  </div>
                </div>
                <p className="text-xs text-[#5B5C5F]">Share this link or send it via email. The customer fills out the form, agrees to T&Cs, and signs digitally.</p>
              </div>
          }
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setFormLinkOpen(false)}>Close</Button>
            {formTokenData && (
              <Button className="bg-[#032352] hover:bg-[#04347A]"
                onClick={() => { setFormLinkOpen(false); setFormLinkEmailOpen(true); }}
                data-testid="send-form-link-email-btn">
                <Mail className="w-4 h-4 mr-2" /> Send via Email
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Credit Limit Dialog (Accept step 1) ─────────────────────────── */}
      <Dialog open={creditLimitDialogOpen} onOpenChange={setCreditLimitDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="credit-limit-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#032352] font-heading flex items-center gap-2">
              <PoundSterling className="w-5 h-5" /> Set Credit Limit
            </DialogTitle>
            <DialogDescription>
              Enter the approved credit limit for <strong>{pendingAcceptApp?.trading_name || pendingAcceptApp?.customer_name}</strong>. You can leave blank to accept without a limit.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <Label className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-1 block">
              Approved Credit Limit (£)
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5B5C5F] font-medium">£</span>
              <Input
                data-testid="credit-limit-input"
                type="number"
                min="0"
                step="500"
                value={creditLimitValue}
                onChange={(e) => setCreditLimitValue(e.target.value)}
                placeholder="e.g. 5000"
                className="pl-7"
                onKeyDown={(e) => { if (e.key === "Enter") confirmCreditLimit(); }}
                autoFocus
              />
            </div>
            <p className="text-xs text-[#5B5C5F] mt-2">This will be printed on the signed PDF and used to track the live credit balance.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="credit-limit-skip-btn" onClick={confirmCreditLimit}>
              Skip (No Limit)
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="credit-limit-confirm-btn"
              onClick={confirmCreditLimit} disabled={creditLimitValue && isNaN(parseFloat(creditLimitValue))}>
              <CheckCircle className="w-4 h-4 mr-2" /> Set Limit &amp; Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── View Application Dialog ──────────────────────────────────────── */}
      <Dialog open={appViewOpen} onOpenChange={setAppViewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-application-dialog">
          <DialogTitle className="text-[#032352] font-heading">Credit Application — {viewedApp?.trading_name || viewedApp?.customer_name}</DialogTitle>
          <DialogDescription className="sr-only">Full credit application details</DialogDescription>
          {viewedApp && (
            <div className="space-y-4 py-2 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-[#5B5C5F]">Trading Name</p><p className="font-medium text-[#032352]">{viewedApp.trading_name || "—"}</p></div>
                <div><p className="text-xs text-[#5B5C5F]">Registration #</p><p className="font-medium text-[#032352]">{viewedApp.registration_number || "—"}</p></div>
                <div><p className="text-xs text-[#5B5C5F]">Trading Style</p><p className="font-medium text-[#032352]">{viewedApp.trading_style || "—"}</p></div>
                <div><p className="text-xs text-[#5B5C5F]">Submitted</p><p className="font-medium text-[#032352]">{fmtDt(viewedApp.submitted_at)}</p></div>
              </div>
              <div><p className="text-xs text-[#5B5C5F]">Registered Address</p><p className="font-medium text-[#032352] whitespace-pre-wrap">{viewedApp.registered_address || "—"}</p></div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <p className="text-xs font-semibold text-[#5B5C5F] uppercase tracking-wider mb-2">Accounts Contact</p>
                <p className="text-[#032352]">{viewedApp.accounts_contact_name || "—"}</p>
                <p className="text-[#5B5C5F] text-xs">{viewedApp.accounts_contact_email || ""}</p>
                <p className="text-[#5B5C5F] text-xs">{viewedApp.accounts_contact_phone || ""}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <p className="text-xs font-semibold text-[#5B5C5F] uppercase tracking-wider mb-2">Buyer Contact</p>
                <p className="text-[#032352]">{viewedApp.buyer_contact_name || "—"}</p>
                <p className="text-[#5B5C5F] text-xs">{viewedApp.buyer_contact_email || ""}</p>
                <p className="text-[#5B5C5F] text-xs">{viewedApp.buyer_contact_phone || ""}</p>
              </div>
              {viewedApp.signature_base64 && (
                <div>
                  <p className="text-xs text-[#5B5C5F] uppercase tracking-wider mb-1">Signature</p>
                  <img src={viewedApp.signature_base64} alt="Applicant signature"
                    className="border border-slate-200 rounded-lg bg-white max-h-24 object-contain"
                    data-testid="application-signature-preview" />
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => { setAppViewOpen(false); openAcceptModal(viewedApp); }}
                  data-testid="accept-from-view-btn">
                  <CheckCircle className="w-4 h-4 mr-2" /> Accept Application
                </Button>
                <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50"
                  onClick={() => openRevertModal(viewedApp)} data-testid="revert-from-view-btn">
                  <RotateCcw className="w-4 h-4 mr-2" /> Revert
                </Button>
                <Button variant="outline" className="border-slate-300 text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                  onClick={() => { setAppViewOpen(false); setRemoveAppTarget({ customer_id: viewedApp.customer_id, name: viewedApp.trading_name || viewedApp.customer_name }); }}
                  data-testid="remove-from-view-btn">
                  <X className="w-4 h-4 mr-2" /> Remove
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Email Preview — Send Form Link ──────────────────────────────── */}
      <EmailPreviewModal
        open={formLinkEmailOpen}
        onClose={() => setFormLinkEmailOpen(false)}
        title="Send Credit Application Link"
        toEmail={formLinkCustomer?.email || ""}
        customerEmail={formLinkCustomer?.email || ""}
        docType="account_form_link"
        invoice={{ customer_name: formLinkCustomer?.name || "" }}
        extraVars={{ form_link: formTokenData?.form_url || "", customer_name: formLinkCustomer?.name || "" }}
        onSend={handleSendFormLink}
        sending={formLinkEmailSending}
      />

      {/* ── Email Preview — Accept Application ─────────────────────────── */}
      <EmailPreviewModal
        open={acceptEmailOpen}
        onClose={() => setAcceptEmailOpen(false)}
        title="Send Approval Confirmation"
        toEmail={acceptTarget?.accounts_contact_email || ""}
        customerEmail={acceptTarget?.accounts_contact_email || ""}
        docType="account_form_accepted"
        invoice={{ customer_name: acceptTarget?.customer_name || "" }}
        extraVars={{ trading_name: acceptTarget?.trading_name || "", customer_name: acceptTarget?.customer_name || "" }}
        attachmentName="CreditApplication.pdf"
        onSend={handleAccept}
        sending={acceptEmailSending}
      />

      {/* ── Email Preview — Revert Application ─────────────────────────── */}
      <EmailPreviewModal
        open={revertEmailOpen}
        onClose={() => setRevertEmailOpen(false)}
        title="Return Application to Customer"
        toEmail={revertTarget?.app?.accounts_contact_email || ""}
        customerEmail={revertTarget?.app?.accounts_contact_email || ""}
        docType="account_form_reverted"
        invoice={{ customer_name: revertTarget?.app?.customer_name || "" }}
        extraVars={{ form_link: revertTarget?.formUrl || "", customer_name: revertTarget?.app?.customer_name || "" }}
        onSend={handleRevert}
        sending={revertEmailSending}
      />

      {/* ── Delete Confirm ──────────────────────────────────────────────── */}
      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        loading={deleting}
        title={`Delete "${deleteTarget?.name}"?`}
        description="This will permanently delete the account and all its data. This cannot be undone."
      />

      {/* ── Remove Registration Confirm ──────────────────────────────── */}
      <ConfirmDialog
        open={!!removeAppTarget}
        onCancel={() => setRemoveAppTarget(null)}
        onConfirm={confirmRemoveApp}
        loading={removingApp}
        title={`Remove registration for "${removeAppTarget?.name}"?`}
        description="This will remove the Registered status and credit application from this account. The account itself will not be deleted."
      />

      {/* ── Edit Credit Limit Dialog ─────────────────────────────────── */}
      <Dialog open={editLimitOpen} onOpenChange={(o) => { if (!o) setEditLimitOpen(false); }}>
        <DialogContent className="max-w-sm" data-testid="edit-credit-limit-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#032352] font-heading flex items-center gap-2">
              <PoundSterling className="w-5 h-5" /> Edit Credit Limit
            </DialogTitle>
            <DialogDescription>
              Update the credit limit for <strong>{editLimitCustomer?.name}</strong>. Leave blank to remove the limit.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <Label className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-1 block">Credit Limit (£)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5B5C5F] font-medium">£</span>
              <Input
                data-testid="edit-credit-limit-input"
                type="number"
                min="0"
                step="500"
                value={editLimitValue}
                onChange={(e) => setEditLimitValue(e.target.value)}
                placeholder="e.g. 5000"
                className="pl-7"
                onKeyDown={(e) => { if (e.key === "Enter") saveEditLimit(); }}
                autoFocus
              />
            </div>
            <p className="text-xs text-[#5B5C5F] mt-2">Leave blank to remove the credit limit from this account.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditLimitOpen(false)}>Cancel</Button>
            <Button className="bg-[#032352] hover:bg-[#04347A]" data-testid="save-credit-limit-btn" onClick={saveEditLimit}>
              Save Limit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
